/* fruvi – Produktseite: Größe, Menge, Kaufart, Intervall, PLZ → Liefertermin, Cross-Sell, Warenkorb */

if (!customElements.get('fruvi-product')) {
  const formatMoney = (cents) =>
    (cents / 100).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';

  class FruviProduct extends HTMLElement {
    connectedCallback() {
      this.data = JSON.parse(this.querySelector('[data-fr-product-json]').textContent);
      this.form = this.querySelector('[data-fr-form]');
      this.stickyBtn = this.querySelector('[data-fr-sticky-btn]');

      this.state = {
        variantId: Number(this.form?.querySelector('[data-fr-variant-id]')?.value),
        qty: 1,
        abo: !this.form?.querySelector('[data-fr-plan-id]')?.disabled && !!this.form?.querySelector('[data-fr-plan-id]'),
        planId: this.form?.querySelector('[data-fr-plan-id]')?.value || null,
      };

      this.bindWeeks();
      this.bindSticky();
      this.bindGallery();
      // „+ Dazu“ legt das Produkt sofort einzeln in den Warenkorb (Delegation, damit es auch nach Neuzeichnen greift)
      this.addEventListener('click', (event) => {
        const btn = event.target.closest('[data-fr-addon]');
        if (btn && this.contains(btn)) this.addAddon(btn);
      });
      // Buttons zeigen den echten Warenkorb-Stand: beim Laden, nach jeder Warenkorb-Änderung (auch Entfernen im Drawer)
      // und wenn die Seite aus dem Browser-Cache zurückkommt
      this.syncAddons();
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribeCart = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          // Eigenes Hinzufügen hat Buttons und Drawer schon aktualisiert
          if (event?.fruvi) return;
          this.cartJson = null;
          this.syncAddons(event?.cartData);
        });
      }
      this.onPageShow = (event) => {
        if (!event.persisted) return;
        this.cartJson = null;
        this.syncAddons();
      };
      window.addEventListener('pageshow', this.onPageShow);
      this.onCartAttrs = () => (this.cartJson = null);
      document.addEventListener('fruvi:cart-attrs', this.onCartAttrs);
      if (!this.form) return;

      this.querySelectorAll('[data-fr-size]').forEach((btn) =>
        btn.addEventListener('click', () => {
          this.state.variantId = Number(btn.dataset.frSize);
          this.update();
        })
      );
      this.querySelectorAll('[data-fr-buytype]').forEach((btn) =>
        btn.addEventListener('click', () => {
          this.state.abo = btn.dataset.frBuytype === 'abo';
          this.update();
        })
      );
      this.querySelectorAll('[data-fr-plan]').forEach((btn) =>
        btn.addEventListener('click', () => {
          this.state.planId = btn.dataset.frPlan;
          this.update();
        })
      );
      this.querySelectorAll('[data-fr-qty]').forEach((btn) =>
        btn.addEventListener('click', () => {
          this.state.qty = Math.min(9, Math.max(1, this.state.qty + Number(btn.dataset.frQty)));
          this.update();
        })
      );
      this.form.addEventListener('submit', (event) => this.onSubmit(event));

      this.onPlz = () => this.update();
      document.addEventListener('fruvi:plz', this.onPlz);
      this.update();
      this.loadCartDate();
    }

    disconnectedCallback() {
      document.removeEventListener('fruvi:plz', this.onPlz);
      window.removeEventListener('pageshow', this.onPageShow);
      document.removeEventListener('fruvi:cart-attrs', this.onCartAttrs);
      this.unsubscribeCart?.();
    }

    /* Warenkorb einmal laden und kurz merken: Cross-Sell-Status und Liefertermin teilen sich denselben Abruf.
       Gleichzeitige Aufrufe warten auf dieselbe Anfrage. */
    fetchCart(maxAgeMs = 0) {
      const now = Date.now();
      if (this.cartJson && now - this.cartAt <= maxAgeMs) return Promise.resolve(this.cartJson);
      if (this.cartPromise) return this.cartPromise;
      this.cartPromise = fetch(`${window.routes?.cart_url || '/cart'}.js`, { headers: { Accept: 'application/json' } })
        .then((r) => r.json())
        .then((cart) => this.rememberCart(cart))
        .finally(() => (this.cartPromise = null));
      return this.cartPromise;
    }

    rememberCart(cart) {
      if (cart && Array.isArray(cart.items)) {
        this.cartJson = cart;
        this.cartAt = Date.now();
      }
      return cart;
    }

    async syncAddons(cartData) {
      const buttons = this.querySelectorAll('[data-fr-addon]');
      if (!buttons.length) return;
      try {
        // Vollständiger Warenkorb aus dem Ereignis (z. B. Menge im Drawer geändert) spart den erneuten Abruf
        const cart = cartData && Array.isArray(cartData.items) ? this.rememberCart(cartData) : await this.fetchCart(1500);
        const inCart = new Set((cart.items || []).map((item) => String(item.variant_id || item.id)));
        buttons.forEach((btn) => {
          if (btn.getAttribute('aria-busy') === 'true') return;
          const on = inCart.has(String(btn.dataset.frAddon));
          btn.setAttribute('aria-pressed', String(on));
          btn.textContent = on ? 'Im Warenkorb' : '+ Dazu';
        });
      } catch (e) {}
    }

    get plz() {
      return window.fruviPlz?.state || {};
    }

    get plzOk() {
      return this.plz.status === 'ok' && (this.plz.dates || []).length > 0;
    }

    get variant() {
      return this.data.variants.find((v) => v.id === this.state.variantId) || this.data.variants[0];
    }

    // Termin, der im Warenkorb schon steht (gleiche PLZ, noch lieferbar), sonst der nächste mögliche
    deliveryIso() {
      if (!this.plzOk) return null;
      const dates = window.fruviPlz.nextDates(62).map((d) => d.iso);
      const cart = this.cartDate;
      if (cart && cart.plz === this.plz.plz && dates.includes(cart.iso)) return cart.iso;
      return dates[0] || null;
    }

    async loadCartDate(maxAgeMs = 0) {
      try {
        const cart = await this.fetchCart(maxAgeMs);
        const attrs = cart.attributes || {};
        const attr = window.fruviConfig?.deliveryAttribute || 'Liefertermin';
        const iso = cart.item_count > 0 ? window.fruviPlz.parseDate(attrs[attr]) : null;
        this.cartDate = iso ? { iso, plz: String(attrs.PLZ || attrs['Liefer-PLZ'] || '') } : null;
        if (this.form) this.update();
      } catch (e) {
        this.cartDate = null;
      }
    }

    unitPrice() {
      const v = this.variant;
      if (this.state.abo && this.state.planId && v.plans[this.state.planId] !== undefined) return v.plans[this.state.planId];
      return v.price;
    }

    update() {
      const v = this.variant;
      const setActive = (selector, test) =>
        this.querySelectorAll(selector).forEach((btn) => {
          const active = test(btn);
          btn.classList.toggle('is-active', active);
          btn.setAttribute('aria-checked', String(active));
        });

      setActive('[data-fr-size]', (btn) => Number(btn.dataset.frSize) === v.id);
      setActive('[data-fr-buytype]', (btn) => (btn.dataset.frBuytype === 'abo') === this.state.abo);
      this.filterPlans();
      setActive('[data-fr-plan]', (btn) => btn.dataset.frPlan === String(this.state.planId));

      // Formularwerte
      this.form.querySelector('[data-fr-variant-id]').value = v.id;
      this.form.querySelector('[data-fr-qty-input]').value = this.state.qty;
      const planInput = this.form.querySelector('[data-fr-plan-id]');
      if (planInput) {
        planInput.disabled = !this.state.abo;
        if (this.state.planId) planInput.value = this.state.planId;
      }
      const interval = this.querySelector('[data-fr-interval]');
      if (interval) interval.hidden = !this.state.abo;

      // Preis
      const total = this.unitPrice() * this.state.qty;
      this.querySelectorAll('[data-fr-price]').forEach((el) => (el.textContent = formatMoney(total)));
      const unit = this.querySelector('[data-fr-unit]');
      if (unit) unit.textContent = this.state.abo ? 'je Lieferung' : 'einmalig';
      const pause = this.querySelector('[data-fr-pause]');
      if (pause) pause.textContent = this.state.abo ? 'Jederzeit pausieren oder kündigen.' : 'Kein Abo. Einfach einmal bestellen.';
      this.querySelector('[data-fr-qty-value]').textContent = this.state.qty;
      const stickySize = this.querySelector('[data-fr-sticky-size]');
      if (stickySize) stickySize.textContent = v.title;

      // PLZ und Liefertermin werden im Warenkorb gewählt. Ist die PLZ schon bekannt, hier der nächste Termin.
      const hint = this.querySelector('[data-fr-dates-hint]');
      const next = this.querySelector('[data-fr-next-date]');
      const iso = this.deliveryIso();
      hint.hidden = !!iso;
      next.hidden = !iso;
      if (iso) next.querySelector('[data-fr-next-date-value]').textContent = window.fruviPlz.longDate(iso);

      this.querySelectorAll('[data-fr-buy]').forEach((btn) => (btn.disabled = !v.available));
    }

    // Appstle legt je Rhythmus teils mehrere Pläne an (z. B. je Variante). Zeigen: nur Pläne der
    // aktuellen Variante, jeden Namen einmal. Ist der gewählte Plan versteckt, auf den sichtbaren gleichen Namens wechseln.
    filterPlans() {
      const buttons = [...this.querySelectorAll('[data-fr-plan]')];
      if (!buttons.length) return;
      const allocations = this.variant.plans || {};
      const hasAllocations = Object.keys(allocations).length > 0;
      const label = (btn) =>
        btn.dataset.frPlanKey || btn.firstElementChild?.textContent.replace(/[\s\u200b-\u200d\ufeff]+/g, ' ').trim().toLowerCase() || '';
      const selectedLabel = label(buttons.find((b) => b.dataset.frPlan === String(this.state.planId)) || buttons[0]);
      const seen = new Map();
      buttons.forEach((btn) => {
        const key = label(btn);
        const allowed = !hasAllocations || allocations[btn.dataset.frPlan] !== undefined;
        const show = allowed && !seen.has(key);
        if (show) seen.set(key, btn.dataset.frPlan);
        btn.hidden = !show;
      });
      const current = buttons.find((b) => b.dataset.frPlan === String(this.state.planId));
      if (!current || current.hidden) {
        this.state.planId = seen.get(selectedLabel) || seen.values().next().value || this.state.planId;
      }
    }

    bindGallery() {
      const main = this.querySelector('[data-fr-gallery-main] img');
      const thumbs = this.querySelectorAll('[data-fr-thumb]');
      if (!main || !thumbs.length) return;
      thumbs.forEach((thumb) =>
        thumb.addEventListener('click', () => {
          main.removeAttribute('srcset');
          main.srcset = thumb.dataset.srcset;
          main.src = thumb.dataset.src;
          main.alt = thumb.dataset.alt || '';
          thumbs.forEach((t) => {
            const active = t === thumb;
            t.classList.toggle('is-active', active);
            t.setAttribute('aria-pressed', String(active));
          });
        })
      );
    }

    showMessage(text) {
      const msg = this.querySelector('[data-fr-msg]');
      msg.textContent = text;
      msg.hidden = !text;
    }

    async onSubmit(event) {
      event.preventDefault();
      await this.loadCartDate(60000);
      // Liefertermin nur als Warenkorb-Attribute (Liefertermin, Liefertermin_Anzeige, PLZ), keine Positions-Eigenschaften.
      // PLZ bekannt: vorhandener Warenkorb-Termin bleibt, sonst der nächste mögliche.
      // PLZ unbekannt: ohne Termin in den Warenkorb, dort werden PLZ und Termin abgefragt (Kasse bis dahin gesperrt).
      const iso = this.deliveryIso();
      // Nach jedem Hinzufügen öffnet sich der Warenkorb-Drawer (auch bei „Jetzt bestellen“ und der mobilen Kaufleiste),
      // damit Liefertermin und Summe vor der Kasse sichtbar sind. Nur ohne Drawer geht es direkt weiter.
      const drawer = document.querySelector('cart-drawer');
      let action = event.submitter?.dataset.frBuy || 'cart';
      if (drawer || !iso) action = 'cart';
      this.showMessage('');

      const formData = new FormData(this.form);
      const properties = {};
      for (const [key, value] of formData.entries()) {
        const match = key.match(/^properties\[(.+)\]$/);
        if (match) properties[match[1]] = value;
      }
      const main = { id: this.variant.id, quantity: this.state.qty, properties };
      if (this.state.abo && this.state.planId) main.selling_plan = Number(this.state.planId);
      const items = [main];

      const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
      const body = { items };
      if (cart && action === 'cart') {
        body.sections = cart.getSectionsToRender().map((s) => s.id);
        body.sections_url = window.location.pathname;
        cart.setActiveElement(event.submitter);
      }

      const buttons = this.querySelectorAll('[data-fr-buy]');
      buttons.forEach((b) => b.setAttribute('aria-busy', 'true'));
      try {
        // Ein Termin pro Warenkorb, gespeichert als Attribute – nur schreiben, wenn er fehlt oder sich ändert
        const saved = this.cartDate;
        if (iso && !(saved && saved.iso === iso && saved.plz === this.plz.plz)) {
          await window.fruviPlz.saveDelivery(iso, this.plz.plz);
        }
        const response = await fetch(`${window.routes?.cart_add_url || '/cart/add'}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body),
        });
        const result = await response.json();
        if (result.status) {
          this.showMessage(result.description || result.message || 'Das hat leider nicht geklappt.');
          return;
        }
        if (action === 'checkout') {
          window.location = '/checkout';
          return;
        }
        if (!cart) {
          window.location = window.routes?.cart_url || '/cart';
          return;
        }
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', fruvi: true, productVariantId: this.variant.id, cartData: result });
        }
        if (iso) this.cartDate = { iso, plz: this.plz.plz };
        this.cartJson = null;
        this.showMessage('Im Warenkorb. Schön, dass du dabei bist.');
        cart.classList.remove('is-empty');
        cart.renderContents(result);
      } catch (e) {
        console.error(e);
        this.showMessage('Das hat leider nicht geklappt. Bitte versuche es noch einmal.');
      } finally {
        buttons.forEach((b) => b.removeAttribute('aria-busy'));
      }
    }

    async addAddon(btn) {
      const id = Number(btn.dataset.frAddon);
      if (!id || btn.getAttribute('aria-busy') === 'true') return;
      btn.setAttribute('aria-busy', 'true');
      btn.textContent = 'Wird hinzugefügt …';
      const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
      const body = { items: [{ id, quantity: 1 }] };
      if (cart) {
        body.sections = cart.getSectionsToRender().map((section) => section.id);
        body.sections_url = window.location.pathname;
        cart.setActiveElement(btn);
      }
      try {
        // Termin wie beim Box-Kauf: vorhandenen Warenkorb-Termin behalten, sonst nächsten möglichen setzen (nur mit bekannter PLZ)
        // Warenkorb vom Seitenaufruf wiederverwenden (bis 60 s alt), statt ihn vor jedem Klick neu zu laden
        await this.loadCartDate(60000);
        const iso = this.deliveryIso();
        // Termin nur schreiben, wenn er im Warenkorb fehlt oder sich ändert – meist steht er schon dort
        const saved = this.cartDate;
        if (iso && !(saved && saved.iso === iso && saved.plz === this.plz.plz)) {
          await window.fruviPlz.saveDelivery(iso, this.plz.plz);
          this.cartDate = { iso, plz: this.plz.plz };
        }
        const response = await fetch(`${window.routes?.cart_add_url || '/cart/add'}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body),
        });
        const result = await response.json();
        if (result.status) {
          this.showMessage(result.description || result.message || 'Das hat leider nicht geklappt.');
          btn.textContent = btn.getAttribute('aria-pressed') === 'true' ? 'Im Warenkorb' : '+ Dazu';
          return;
        }
        // Zwischenspeicher ist jetzt veraltet, die Buttons kennen den neuen Stand aber schon
        this.cartJson = null;
        btn.setAttribute('aria-pressed', 'true');
        btn.textContent = 'Im Warenkorb';
        if (!cart) {
          window.location = window.routes?.cart_url || '/cart';
          return;
        }
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', fruvi: true, productVariantId: id, cartData: result });
        }
        cart.classList.remove('is-empty');
        cart.renderContents(result);
      } catch (e) {
        console.error(e);
        btn.textContent = btn.getAttribute('aria-pressed') === 'true' ? 'Im Warenkorb' : '+ Dazu';
        this.showMessage('Das hat leider nicht geklappt. Bitte versuche es noch einmal.');
      } finally {
        btn.removeAttribute('aria-busy');
      }
    }

    bindWeeks() {
      const tabs = this.querySelectorAll('[data-fr-week]');
      tabs.forEach((tab) =>
        tab.addEventListener('click', () => {
          tabs.forEach((t) => {
            const active = t === tab;
            t.classList.toggle('is-active', active);
            t.setAttribute('aria-selected', String(active));
          });
          this.querySelectorAll('[data-fr-week-panel]').forEach(
            (panel) => (panel.hidden = panel.dataset.frWeekPanel !== tab.dataset.frWeek)
          );
        })
      );
    }

    bindSticky() {
      this.stickyBtn?.addEventListener('click', () => {
        if (this.data.inSeason === false && this.data.isSorte) {
          const input = this.querySelector('[data-fr-notify-input]');
          input?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          input?.focus();
          return;
        }
        this.form?.requestSubmit(this.querySelector('[data-fr-buy="checkout"]'));
      });
    }
  }

  customElements.define('fruvi-product', FruviProduct);
}
